import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { Unbrand } from "@core/domain/types/unbrand.type";
import type { ModelError, PartialValid } from "@iot-platform/core";
import {
    NonRecuperableModelError,
    RecuperableModelError,
    partialSafeParse,
} from "@iot-platform/core";
import { merge } from "es-toolkit";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

const LayerTypeLegendSchema = z.object({
    description: z.string().trim(),
    unit: z.string().trim(),
    min: z.number(),
    max: z.number(),
    colorRamp: z.array(
        z.object({
            value: z.number(),
            color: z.string().trim(),
        }),
    ),
});

export type LayerTypeLegend = z.output<typeof LayerTypeLegendSchema>;

const LayerTypeSchema = z
    .object({
        code: z.string().trim(),
        name: z.string().trim(),
        source: z.string().trim(),
        legend: LayerTypeLegendSchema.optional(),
    })
    .brand<"LayerType">();

export type LayerTypeInputProps = z.input<typeof LayerTypeSchema>;
type LayerTypeProps = z.output<typeof LayerTypeSchema>;

export class LayerType {
    private constructor(private readonly props: Readonly<LayerTypeProps>) {}

    get code(): string {
        return this.props.code;
    }

    get name(): string {
        return this.props.name;
    }

    get source(): string {
        return this.props.source;
    }

    get legend(): LayerTypeLegend | undefined {
        return this.props.legend;
    }

    static validate(
        props: Readonly<MaybeValidProps<LayerTypeInputProps>>,
    ): Result<LayerTypeProps, z.ZodError<LayerTypeProps>> {
        const result = LayerTypeSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<LayerTypeInputProps>>,
    ): Result<LayerTypeProps, PartialValid<typeof LayerType>> {
        const result = partialSafeParse(LayerTypeSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: Readonly<LayerTypeInputProps>,
    ): Result<LayerType, z.ZodError<LayerTypeProps>> {
        return LayerType.validate(props).map(
            (validatedProps) => new LayerType(validatedProps),
        );
    }

    static createWithRecovery(
        props: LayerTypeInputProps,
    ): Result<LayerType, ModelError<typeof LayerType>> {
        return LayerType.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.code)
                    return new NonRecuperableModelError();

                const defaultProps: Unbrand<LayerTypeProps> = {
                    code: "",
                    name: "",
                    source: "",
                    legend: undefined,
                };

                const recoveryProps: Unbrand<LayerTypeProps> = merge(
                    defaultProps,
                    error.validProps,
                );

                return new RecuperableModelError<typeof LayerType>(
                    new LayerType(recoveryProps as LayerTypeProps),
                    error.errors,
                );
            })
            .map((validatedProps) => new LayerType(validatedProps));
    }

    toCreateProps(): LayerTypeInputProps {
        return {
            code: this.props.code,
            name: this.props.name,
            source: this.props.source,
            legend: this.props.legend,
        };
    }
}
